import { Stack } from '@mui/joy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCard } from 'core/components';
import { AlternateVersionHelper } from 'core/helpers';
import { getAddressToDisplay, getValue } from 'core/utils';
import {
  ContactGroupAddressType,
  UpdateQuoteAddress,
} from 'quotes/components/CustomerQuoteDetails/Tabs/CustomerQuoteBaseInformation';
import { QuoteEntityType } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';

import { SectionWarning } from './SectionWarning';
import { getInactiveBillToProjectLinkMessage } from './utils';

interface IProps {
  quote: ICustomerQuote;
  updateQuote: (updatedQuote?: ICustomerQuote) => void;
  entityType?: QuoteEntityType;
  isEditable?: boolean;
  onChangeEmployees?: () => void;
}

export const BillToInfo = ({
  quote,
  updateQuote,
  entityType = QuoteEntityType.QUOTE,
  isEditable = false,
  onChangeEmployees,
}: IProps) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });
  const isArNumberMissing =
    isAlternateVersionEnabled &&
    QuoteEntityType.ORDER === entityType &&
    !quote.billToAddress?.accountsReceivableId;
  const inactiveProjectLinkMessage = getInactiveBillToProjectLinkMessage(quote, entityType);

  const handleOnClose = (updatedQuote?: ICustomerQuote) => {
    setIsEditOpen(false);
    updateQuote(updatedQuote);
  };

  return (
    <>
      <InfoCard
        title={t('titleBillTo')}
        items={[
          {
            label: getValue(quote, 'billToAddress.name') as string,
            value: getAddressToDisplay(quote?.billToAddress),
            extra:
              isArNumberMissing || inactiveProjectLinkMessage ? (
                <Stack gap={0.5}>
                  {isArNumberMissing && (
                    <SectionWarning message={t('messageArNumberRequiredForOrder')} />
                  )}

                  {inactiveProjectLinkMessage && (
                    <SectionWarning message={inactiveProjectLinkMessage} />
                  )}
                </Stack>
              ) : undefined,
          },
        ]}
        onEdit={() => setIsEditOpen(true)}
        isEditable={isEditable}
      />

      <UpdateQuoteAddress
        isOpen={isEditOpen}
        quote={quote}
        entityType={entityType}
        onClose={handleOnClose}
        addressType={ContactGroupAddressType.BILL_TO}
        onChangeEmployees={onChangeEmployees}
        hasCreateNewLocation={
          !quote?.alternateVersionEnabled || entityType === QuoteEntityType.QUOTE
        }
      />
    </>
  );
};
